import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut,
    RefreshCw,
    Search,
    Trash2,
    X,
    Mail,
    Phone,
    Building2,
    Calendar,
} from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { API } from "@/lib/api";
import {
    INDUSTRIES,
    MAIN_GOALS,
    NEXT_STEPS,
    BENEFITS_STATUS,
} from "@/lib/leads";

const TOKEN_KEY = "hc-admin-token";

const KIND_LABEL = {
    proposal: "Proposal",
    representative: "Representative",
    contact: "Contact",
};
const KIND_TONE = {
    proposal: "border-brand text-navy bg-brand/10",
    representative: "border-foreground text-foreground bg-foreground/5",
    contact: "border-ink-soft text-ink-soft bg-surface",
};

const label = (list, value) => list.find((x) => (x.value ?? x.key) === value)?.label || value || "—";

export default function AdminLeads() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(false);
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [kindFilter, setKindFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);

    const headers = useCallback(() => ({ "X-Admin-Token": token }), [token]);

    const tryAuth = useCallback(async (t) => {
        setChecking(true);
        try {
            const res = await fetch(`${API}/admin/verify`, {
                headers: { "X-Admin-Token": t },
            });
            if (res.ok) {
                localStorage.setItem(TOKEN_KEY, t);
                setToken(t);
                setAuthed(true);
                return true;
            }
            toast.error("Invalid admin token");
            return false;
        } catch (e) {
            toast.error("Could not reach the server.");
            return false;
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        if (token) tryAuth(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        if (!authed) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (kindFilter !== "all") params.set("kind", kindFilter);
            if (search.trim()) params.set("search", search.trim());
            const [statsRes, leadsRes] = await Promise.all([
                fetch(`${API}/admin/stats`, { headers: headers() }),
                fetch(`${API}/admin/leads?${params.toString()}`, { headers: headers() }),
            ]);
            if (statsRes.ok) setStats(await statsRes.json());
            if (leadsRes.ok) setLeads(await leadsRes.json());
        } catch (e) {
            toast.error("Failed to load leads.");
        } finally {
            setLoading(false);
        }
    }, [authed, kindFilter, search, headers]);

    useEffect(() => {
        if (authed) load();
    }, [authed, kindFilter, load]);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this lead permanently?")) return;
        try {
            const res = await fetch(`${API}/admin/leads/${id}`, {
                method: "DELETE",
                headers: headers(),
            });
            if (!res.ok) throw new Error("delete failed");
            toast.success("Lead deleted");
            setSelected(null);
            load();
        } catch (e) {
            toast.error("Could not delete.");
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
        setStats(null);
        setLeads([]);
    };

    if (!authed) {
        return <LoginGate token={token} setToken={setToken} checking={checking} onSubmit={tryAuth} />;
    }

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="page-admin-leads">
            {/* Admin header */}
            <header className="border-b border-border bg-background sticky top-0 z-30" data-testid="admin-header">
                <div className="container-x flex items-center justify-between h-16">
                    <div className="flex items-center gap-6">
                        <Link to="/" data-testid="admin-logo"><Logo /></Link>
                        <span className="text-xs uppercase tracking-[0.18em] text-ink-soft">Lead dashboard</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={load}
                            data-testid="admin-refresh"
                            className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-foreground border border-border px-3 py-2"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                            Refresh
                        </button>
                        <ThemeToggle />
                        <button
                            type="button"
                            onClick={logout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-foreground border border-border px-3 py-2"
                        >
                            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 container-x py-10 space-y-8">
                {/* Stats */}
                <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border" data-testid="admin-stats">
                    <Stat label="Total leads" value={stats?.total ?? "—"} testId="stat-total" />
                    <Stat label="Proposal" value={stats?.by_kind?.proposal ?? "—"} testId="stat-proposal" />
                    <Stat label="Representative" value={stats?.by_kind?.representative ?? "—"} testId="stat-representative" />
                    <Stat label="Last 7 days" value={stats?.last_7_days ?? "—"} testId="stat-7d" />
                </section>

                {/* Filters */}
                <section className="flex flex-col md:flex-row md:items-center gap-4" data-testid="admin-filters">
                    <div className="flex flex-wrap gap-2">
                        {[
                            { v: "all", l: "All" },
                            { v: "proposal", l: "Proposal" },
                            { v: "representative", l: "Representative" },
                            { v: "contact", l: "Contact" },
                        ].map((f) => (
                            <button
                                key={f.v}
                                type="button"
                                onClick={() => setKindFilter(f.v)}
                                data-testid={`filter-${f.v}`}
                                className={`px-4 py-2 border text-sm transition-colors ${
                                    kindFilter === f.v
                                        ? "border-brand bg-brand/10 text-foreground"
                                        : "border-border text-ink-soft hover:text-foreground hover:border-foreground/40"
                                }`}
                            >
                                {f.l}
                            </button>
                        ))}
                    </div>
                    <form
                        onSubmit={(e) => { e.preventDefault(); load(); }}
                        className="md:ml-auto flex items-center gap-2 border border-border px-3 py-2 md:min-w-[280px]"
                    >
                        <Search className="h-4 w-4 text-ink-soft" strokeWidth={1.5} />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="admin-search"
                            placeholder="Search org / name / email"
                            className="w-full bg-transparent focus:outline-none text-sm"
                        />
                    </form>
                </section>

                {/* Table */}
                <section className="border border-border bg-background" data-testid="admin-table">
                    <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-xs uppercase tracking-[0.15em] text-ink-soft border-b border-border bg-surface/50">
                        <div className="col-span-2">Kind</div>
                        <div className="col-span-3">Organization</div>
                        <div className="col-span-3">Contact</div>
                        <div className="col-span-2">Goal</div>
                        <div className="col-span-2 text-right">Submitted</div>
                    </div>

                    {loading && leads.length === 0 ? (
                        <div className="p-10 text-center text-ink-soft" data-testid="admin-loading">Loading leads…</div>
                    ) : leads.length === 0 ? (
                        <div className="p-10 text-center text-ink-soft" data-testid="admin-empty">
                            No leads yet. They'll appear here as people submit proposals or talk to a representative.
                        </div>
                    ) : (
                        <ul className="divide-y divide-border" data-testid="admin-rows">
                            {leads.map((l) => (
                                <li
                                    key={l.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelected(l)}
                                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSelected(l)}
                                    data-testid={`lead-row-${l.id}`}
                                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 hover:bg-surface/60 cursor-pointer transition-colors"
                                >
                                    <div className="md:col-span-2">
                                        <span className={`inline-block text-xs px-2.5 py-1 border ${KIND_TONE[l.kind] || "border-border"}`}>
                                            {KIND_LABEL[l.kind] || l.kind}
                                        </span>
                                    </div>
                                    <div className="md:col-span-3 text-sm">
                                        <p className="text-foreground font-medium">{l.organizationName || "—"}</p>
                                        <p className="text-xs text-ink-soft">{label(INDUSTRIES, l.industry)}</p>
                                    </div>
                                    <div className="md:col-span-3 text-sm">
                                        <p className="text-foreground">{l.contactName || "—"}</p>
                                        <p className="text-xs text-ink-soft">{l.contactEmail || "—"}</p>
                                    </div>
                                    <div className="md:col-span-2 text-sm text-ink-soft">
                                        {label(MAIN_GOALS, l.mainGoal)}
                                    </div>
                                    <div className="md:col-span-2 text-xs text-ink-soft md:text-right">
                                        {l.createdAt ? new Date(l.createdAt).toLocaleString() : "—"}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <p className="text-xs text-ink-soft" data-testid="admin-token-hint">
                    Tip: rotate <code className="text-foreground">ADMIN_TOKEN</code> in <code className="text-foreground">backend/.env</code> when you need to revoke access.
                </p>
            </main>

            {selected && (
                <DetailDrawer
                    lead={selected}
                    onClose={() => setSelected(null)}
                    onDelete={() => onDelete(selected.id)}
                />
            )}
        </div>
    );
}

function Stat({ label, value, testId }) {
    return (
        <div className="bg-background px-6 py-7" data-testid={testId}>
            <p className="text-xs uppercase tracking-[0.15em] text-ink-soft">{label}</p>
            <p className="font-heading text-4xl mt-2 text-foreground">{value}</p>
        </div>
    );
}

function LoginGate({ token, setToken, checking, onSubmit }) {
    const [val, setVal] = useState(token);
    const submit = (e) => {
        e.preventDefault();
        if (!val.trim()) return;
        onSubmit(val.trim());
    };
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6" data-testid="admin-login">
            <div className="w-full max-w-md border border-border bg-background p-10 space-y-7">
                <Link to="/" className="inline-block" data-testid="admin-login-logo"><Logo /></Link>
                <div>
                    <p className="eyebrow mb-2">Lead dashboard</p>
                    <h1 className="h3">Enter the admin token to continue.</h1>
                </div>
                <form onSubmit={submit} className="space-y-5">
                    <label className="block">
                        <span className="block text-xs uppercase tracking-[0.15em] text-ink-soft mb-2">Admin token</span>
                        <input
                            type="password"
                            value={val}
                            onChange={(e) => setVal(e.target.value)}
                            data-testid="admin-token-input"
                            className="w-full bg-transparent border-b border-border py-3 text-foreground focus:outline-none focus:border-brand transition-colors"
                            placeholder="••••••••••••"
                            autoFocus
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={checking}
                        data-testid="admin-token-submit"
                        className="w-full bg-brand text-brand-foreground hover:bg-brand-hover px-6 py-3 rounded-sm text-sm font-medium disabled:opacity-60"
                    >
                        {checking ? "Verifying…" : "Sign in"}
                    </button>
                </form>
                <p className="text-xs text-ink-soft">
                    Set in <code className="text-foreground">backend/.env</code> as <code className="text-foreground">ADMIN_TOKEN</code>.
                </p>
            </div>
        </div>
    );
}

function DetailDrawer({ lead, onClose, onDelete }) {
    useEffect(() => {
        const onEsc = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="lead-detail-drawer">
            <div
                className="flex-1 bg-foreground/30 backdrop-blur-sm"
                onClick={onClose}
                aria-label="Close detail"
            />
            <aside className="w-full max-w-xl bg-background border-l border-border overflow-y-auto p-8 md:p-10 space-y-7">
                <div className="flex items-center justify-between">
                    <span className={`inline-block text-xs px-2.5 py-1 border ${KIND_TONE[lead.kind] || "border-border"}`}>
                        {KIND_LABEL[lead.kind] || lead.kind}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="detail-close"
                        className="text-ink-soft hover:text-foreground"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" strokeWidth={1.5} />
                    </button>
                </div>

                <header className="space-y-2">
                    <p className="eyebrow">{lead.campaign}</p>
                    <h2 className="h3" data-testid="detail-org">{lead.organizationName || "Untitled organization"}</h2>
                    <p className="text-sm text-ink-soft flex items-center gap-2" data-testid="detail-created">
                        <Calendar className="h-4 w-4" strokeWidth={1.5} />
                        {lead.createdAt ? new Date(lead.createdAt).toLocaleString() : "—"}
                    </p>
                </header>

                <section className="space-y-4">
                    <Row icon={Building2} label="Industry" value={label(INDUSTRIES, lead.industry)} />
                    <Row label="Contact name" value={lead.contactName} />
                    <Row icon={Mail} label="Email" value={lead.contactEmail} link={lead.contactEmail ? `mailto:${lead.contactEmail}` : null} />
                    <Row icon={Phone} label="Phone" value={lead.phone} link={lead.phone ? `tel:${lead.phone}` : null} />
                    <Row label="Employees" value={lead.employeeCount} />
                    <Row label="Payroll range" value={lead.payrollRange} />
                    <Row label="Workforce mix" value={lead.workforceMix} />
                    <Row label="Benefits status" value={label(BENEFITS_STATUS, lead.benefitsStatus)} />
                    <Row label="Main goal" value={label(MAIN_GOALS, lead.mainGoal)} />
                    <Row label="Preferred next step" value={label(NEXT_STEPS, lead.preferredNextStep)} />
                    <Row label="Contact path" value={lead.contactPath} />
                    <Row label="Source page" value={lead.sourcePage} />
                    <Row label="States" value={(lead.states || []).join(", ")} />
                </section>

                {(lead.notes || lead.message) && (
                    <section className="space-y-2">
                        <p className="eyebrow">Notes</p>
                        <p className="body text-sm whitespace-pre-wrap" data-testid="detail-notes">
                            {lead.notes || lead.message}
                        </p>
                    </section>
                )}

                <details className="text-xs">
                    <summary className="cursor-pointer text-ink-soft uppercase tracking-[0.15em]">Raw payload</summary>
                    <pre className="mt-3 p-4 bg-surface border border-border overflow-auto">{JSON.stringify(lead, null, 2)}</pre>
                </details>

                <div className="pt-4 border-t border-border flex items-center justify-between">
                    <a
                        href={`mailto:${lead.contactEmail || ""}`}
                        data-testid="detail-email-cta"
                        className="inline-flex items-center bg-brand text-brand-foreground hover:bg-brand-hover px-5 py-2.5 rounded-sm text-sm font-medium"
                    >
                        Reply by email
                    </a>
                    <button
                        type="button"
                        onClick={onDelete}
                        data-testid="detail-delete"
                        className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-destructive"
                    >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete
                    </button>
                </div>
            </aside>
        </div>
    );
}

function Row({ icon: Icon, label, value, link }) {
    const display = value === 0 || value ? String(value) : "—";
    return (
        <div className="grid grid-cols-3 gap-4 items-baseline border-b border-border last:border-b-0 pb-3">
            <span className="text-xs uppercase tracking-[0.15em] text-ink-soft flex items-center gap-2">
                {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />}
                {label}
            </span>
            <span className="col-span-2 text-sm text-foreground break-words">
                {link && value ? (
                    <a href={link} className="hover:text-brand underline underline-offset-4 decoration-border">{display}</a>
                ) : (
                    display
                )}
            </span>
        </div>
    );
}
